import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExclamationTriangle, FaShieldAlt, FaTimes } from 'react-icons/fa';
import {
  findActiveServiceForPanic,
  insertPanicAlert,
  updatePanicAlertLocation,
} from '../../services/api/realtime';
import { resolvePanicAlert } from '../../services/api/admin-ops';
import { useAuth } from '../USER/Auth/AuthContext';
import Swal from 'sweetalert2';
import { SERVICE_STATUSES, PANIC_ALERT_STATUSES } from '../../constants';

/**
 * PanicButton — Botón de emergencia con activación por presión prolongada.
 * Al activar: inserta alerta en panic_alerts, inicia tracking GPS de alta frecuencia
 * y muestra marcador pulsante en el mapa del admin en tiempo real.
 * Si no se pasa serviceId, detecta automáticamente el servicio activo del usuario.
 */
const PanicButton = ({ serviceId = null, className = "", style = {} }) => {
  const { currentUser, isDriver } = useAuth();
  const [pressing, setPressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeAlert, setActiveAlert] = useState(null);
  const [gpsCount, setGpsCount] = useState(0);
  const [resolvedServiceId, setResolvedServiceId] = useState(serviceId);
  const timerRef = useRef(null);
  const watchIdRef = useRef(null);
  const alertIdRef = useRef(null);

  useEffect(() => {
    if (serviceId) {
      setResolvedServiceId(serviceId);
      return;
    }
    const fetchActive = async () => {
      const { data } = await findActiveServiceForPanic(
        currentUser.id,
        isDriver,
        [SERVICE_STATUSES.DRIVER_ASSIGNED, SERVICE_STATUSES.IN_PROGRESS]
      );
      if (data) setResolvedServiceId(data.service_id);
    };
    fetchActive();
  }, [serviceId, isDriver, currentUser]);

  const stopTracking = useCallback(async () => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (alertIdRef.current) {
      await resolvePanicAlert(alertIdRef.current, PANIC_ALERT_STATUSES.RESOLVED);
      alertIdRef.current = null;
    }
    setActiveAlert(null);
    setGpsCount(0);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  const startPress = () => {
    if (activeAlert) return;
    setPressing(true);
    setProgress(0);
    let currentProgress = 0;

    timerRef.current = setInterval(() => {
      currentProgress += 5;
      setProgress(currentProgress);

      if (currentProgress >= 100) {
        clearInterval(timerRef.current);
        triggerAlert();
      }
    }, 100);
  };

  const cancelPress = () => {
    setPressing(false);
    setProgress(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const triggerAlert = async () => {
    setPressing(false);

    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
        });
      });

      const { data, error } = await insertPanicAlert({
        user_id: currentUser.id,
        service_id: resolvedServiceId,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        status: PANIC_ALERT_STATUSES.ACTIVE,
      });

      if (error) throw error;

      alertIdRef.current = data.id;
      setActiveAlert(data);
      setGpsCount(1);

      watchIdRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setGpsCount((c) => c + 1);
          await updatePanicAlertLocation(data.id, latitude, longitude);
        },
        (err) => console.error('GPS watch error:', err),
        { enableHighAccuracy: true, maximumAge: 3000, distanceFilter: 3 }
      );

      Swal.fire({
        icon: 'warning',
        title: '🚨 ¡ALERTA SOS ACTIVADA!',
        html: `
          <div style="text-align: left; font-size: 0.9rem;">
            <p>La central de monitoreo de Bryemma y los administradores han recibido tu alerta prioritaria con transmisión GPS en tiempo real.</p>
            <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 15px;">
              <a href="tel:911" class="btn btn-danger w-100 fw-bold" style="text-decoration: none; padding: 12px; border-radius: 8px; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 1rem;">
                📞 Llamar al 911 (Emergencias Directas)
              </a>
            </div>
            <div style="margin-top: 12px; font-size: 0.8rem; color: #a1a1aa; text-align: center;">
              Tu señal se mantiene activa y transmitiendo coordenadas cada 3 segundos.
            </div>
          </div>
        `,
        showConfirmButton: true,
        confirmButtonText: 'Mantener Alerta en Pantalla',
        confirmButtonColor: '#d33',
        background: 'var(--bry-bg-elevated, #1a1a1a)',
        color: '#ffffff'
      });
    } catch (err) {
      console.error('Panic Alert Error:', err);
      Swal.fire('Error', 'No se pudo enviar la alerta. Llama a emergencias directamente.', 'error');
    }
  };

  return (
    <div className="panic-button-container" style={{ position: 'relative', flex: 1, display: 'flex' }}>
      <AnimatePresence>
        {activeAlert && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 10 }}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: '10px',
              background: 'rgba(211, 51, 51, 0.95)',
              color: 'var(--bry-text-main)',
              padding: '8px 14px',
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 800,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(211, 51, 51, 0.4)',
              zIndex: 100,
            }}
          >
            <motion.div
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FaShieldAlt /> ACTIVA — GPS ACTUALIZANDO ({gpsCount})
            </motion.div>
            <button
              onClick={(e) => { e.stopPropagation(); stopTracking(); }}
              style={{
                marginTop: 4,
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'var(--bry-text-main)',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 9,
                cursor: 'pointer',
              }}
            >
              <FaTimes size={8} /> Cancelar alerta
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pressing && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 10 }}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: '10px',
              background: 'rgba(14, 171, 176, 0.95)',
              color: 'var(--bry-text-main)',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 800,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(14, 171, 176,0.4)',
              zIndex: 100,
              pointerEvents: 'none',
            }}
          >
            MANTÉN PRESIONADO
            <div style={{
              width: '100%', height: 4, background: 'rgba(255,255,255,0.3)',
              borderRadius: 2, marginTop: 4, overflow: 'hidden'
            }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'var(--bry-bg-elevated)', transition: 'width 0.1s linear' }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className={`qa-btn panic-qa-btn flex items-center justify-center gap-1 transition-colors ${className || 'border-2 border-red-500 rounded-full px-4 py-2'}`}
        whileTap={{ scale: 0.95 }}
        onMouseDown={startPress}
        onMouseUp={cancelPress}
        onMouseLeave={cancelPress}
        onTouchStart={startPress}
        onTouchEnd={cancelPress}
        style={{
          background: activeAlert ? '#ef4444' : pressing ? '#ef4444' : (style.background || 'transparent'),
          color: activeAlert || pressing ? 'var(--bry-white)' : (style.color || '#ef4444'),
          position: 'relative',
          ...style
        }}
      >
        {activeAlert && (
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
            style={{
              position: 'absolute',
              top: -4, right: -4,
              width: 12, height: 12,
              borderRadius: '50%',
              background: '#ff4444',
              border: '2px solid #fff',
            }}
          />
        )}
        <FaExclamationTriangle className="text-current" />
        <span className="font-bold">Pánico</span>
      </motion.button>
    </div>
  );
};

export default PanicButton;
